import {UiFlashContainer, UiFlashItem} from "mu-widget/lib/components/UiFlash";
import {ErrorList} from "mu-widget/lib/utils/ErrorList";
import {makeHtmlElement} from "mu-widget/lib/utils/utils";
import {energyUnitOpts, frontAppConfig} from "./FrontAppConfig";
import {Nutrients} from "./types";

export function cycleCssClasses<TNames extends string=string>(element: HTMLElement, classes: TNames[], force: TNames|null = null): TNames {
    if (force !== null) {
        classes.forEach(className => {
            if (element.classList.contains(className)) {
                element.classList.remove(className);
            }
        });
        element.classList.add(force);
        return force
    } else {
        const currentIndex = classes.findIndex(className => element.classList.contains(className));

        if (currentIndex !== -1) {
            const newIndex = (currentIndex + 1) % classes.length;
            const newClass = classes[newIndex];

            element.classList.remove(classes[currentIndex]);
            element.classList.add(newClass);
            return newClass;
        } else {
            element.classList.add(classes[0]);
            return classes[0];
        }
    }
}

export function redirect(url: string) {
    document.location.href = url;
}

export function addFlash(message: string|HTMLElement, theme?: 'error' | 'ok' | string): UiFlashItem {
    if (typeof message === 'string') {
        return UiFlashContainer.add(message, theme);
    } else {
        const flashMessage = UiFlashContainer.add('', theme);
        flashMessage.ui.message.appendChild(message);
        return flashMessage;
    }
}

export function errorsToHtml(errls: ErrorList, label: string): HTMLElement
{
    const eContainer = document.createElement("div");
    if (label) eContainer.append(label);
    const eList = makeHtmlElement('ul');
    eContainer.append(eList);
    for (const message of errls) {
        eList.append(makeHtmlElement('li', {}, message.message));
    }
    return eContainer;
}

export function getOptionTextByValue(element: HTMLSelectElement,value: string): string|null {
    if (!element) throw new Error('element is required');

    for (let i = 0; i < element.options.length; i++) {
        const option = element.options[i];
        if (option.value === value) {
            return option.text;
        }
    }

    return null;
}

export function urlEqual(url1: string, url2: string): boolean {
    const normalizePath = (path: string): string => {
        if (path.length > 1 && path.endsWith('/')) {
            return path.slice(0, -1);
        }
        return path;
    };

    const hasProtocol = (url: string): boolean => {
        return url.startsWith('http://') || url.startsWith('https://');
    };

    const isRelativePath = (url: string): boolean => {
        return url.startsWith('/');
    };

    const url1HasProtocol = hasProtocol(url1);
    const url2HasProtocol = hasProtocol(url2);
    const url1IsRelative = isRelativePath(url1);
    const url2IsRelative = isRelativePath(url2);

    if ((url1IsRelative && url2HasProtocol) || (url2IsRelative && url1HasProtocol)) {
        let pathWithProtocol: string;
        let relativePath: string;

        if (url1HasProtocol) {
            pathWithProtocol = url1;
            relativePath = url2;
        } else {
            pathWithProtocol = url2;
            relativePath = url1;
        }

        try {
            const parsedUrlWithProtocol = new URL(pathWithProtocol);
            const fullPathFromProtocolUrl = parsedUrlWithProtocol.pathname + parsedUrlWithProtocol.search;

            return normalizePath(fullPathFromProtocolUrl) === normalizePath(relativePath);
        } catch (e) {
            console.error("Chyba při parsování URL s protokolem:", e);
            return false;
        }
    }

    return normalizePath(url1) === normalizePath(url2);
}

export function formatEnergy(value: number, addUnit: boolean = true, noSpace: boolean = false): string
{
    const opts = energyUnitOpts[frontAppConfig.energyUnit];
    value = Math.round(value * opts.coefficient);

    return Number.isNaN(value)
        ? "-"
        : value.toLocaleString('cs-CZ', { useGrouping: !noSpace }) + (addUnit ? opts.name : '');

}

export function elementToAiText(root: Element): string {
    const parts: string[] = [];

    const isHidden = (el: Element): boolean =>
        el instanceof HTMLElement && el.style.display === 'none';

    const dlToText = (dl: Element): string => {
        const lines: string[] = [];
        const children = Array.from(dl.children);
        for (let i = 0; i < children.length; i++) {
            const dt = children[i];
            if (dt.tagName !== 'DT') continue;
            const dd = children[i + 1]?.tagName === 'DD' ? children[i + 1] : null;
            if (isHidden(dt) || (dd && isHidden(dd))) continue;
            const label = dt.textContent?.trim() ?? '';
            const value = dd?.textContent?.trim() ?? '';
            if (label || value) lines.push(value ? `${label} ${value}` : label);
        }
        return lines.join('\n');
    };

    const tableToText = (table: Element): string => {
        const lines: string[] = [];
        for (const row of Array.from(table.querySelectorAll('tr'))) {
            if (isHidden(row)) continue;
            const cellText = Array.from(row.children)
                .filter(cell => !isHidden(cell))
                .map(cell => cell.textContent?.trim() ?? '')
                .join(' | ');
            if (cellText.replace(/\|/g, '').trim()) lines.push(cellText);
        }
        return lines.join('\n');
    };

    const visit = (el: Element) => {
        if (isHidden(el)) return;
        if (el.tagName === 'A' || el.tagName === 'BUTTON') return;
        if (el.tagName === 'TABLE') { parts.push(tableToText(el)); return; }
        if (el.tagName === 'DL') { parts.push(dlToText(el)); return; }
        if (el.children.length === 0) {
            const text = el.textContent?.trim();
            if (text) parts.push(text);
            return;
        }
        for (const child of Array.from(el.children)) visit(child);
    };

    visit(root);
    return parts.filter(p => p.trim().length > 0).join('\n\n');
}

export function formatWeight(value: number, addUnit: boolean = true): string
{
    value = Math.round(value);

    return Number.isFinite(value)
        ? value.toLocaleString() + (addUnit ? 'g' : '')
        : '-';
}

export function memoize<Args extends any[], R>(callback: (...args: Args) => R): (...args: Args) => R {
    const cache = new Map<string, R>();

    return (...args: Args): R => {
        const key = JSON.stringify(args);

        if (cache.has(key)) {
            return cache.get(key)!;
        }

        const result = callback(...args);
        cache.set(key, result);
        return result;
    };
}

/**
 * Výsledek volání memoizované funkce s informacemi o stavu cache.
 */
interface MemoChangeResult<R> {
    /** True, pokud se argumenty liší od úplně posledního volání. */
    changed: boolean;
    /** True, pokud byl výsledek nalezen v historii (v Mapě). */
    fromCache: boolean;
    /** Samotný výsledek funkce. */
    response: R;
}

/**
 * Funkce, která si pamatuje všechny předchozí výsledky a zároveň
 * detekuje změnu oproti bezprostředně předchozímu volání.
 */
export function memoizeWithChange<Args extends any[], R>(
    callback: (...args: Args) => R
): (...args: Args) => MemoChangeResult<R> {
    const fullCache = new Map<string, R>();

    let lastArgs: Args | null = null;

    return (...args: Args): MemoChangeResult<R> => {
        const key = JSON.stringify(args);

        const isSameAsLast =
            lastArgs !== null &&
            args.length === lastArgs.length &&
            args.every((arg, index) => arg === lastArgs![index]);

        const hasInCache = fullCache.has(key);

        let result: R;

        if (hasInCache) {
            result = fullCache.get(key)!;
        } else {
            result = callback(...args);
            fullCache.set(key, result);
        }

        const output: MemoChangeResult<R> = {
            changed: !isSameAsLast,
            fromCache: hasInCache,
            response: result,
        };

        lastArgs = args;

        return output;
    };
}

/**
 * Converts a value to an integer or returns null if conversion is not possible.
 * * @param value - The value to be converted.
 * @returns The converted integer or null.
 */
export function tryParseInt(value: any): number | null {
    if (value === null || value === undefined || Array.isArray(value) || typeof value === 'boolean') {
        return null;
    }
    const parsed = Number(value);

    if (!Number.isInteger(parsed)) {
        return null;
    }

    return parsed;
}

/**
 * Sleduje, zda se daný element dostal do viewportu.
 * @param element - HTML element, který chceme sledovat
 * @param callback - Funkce, která se provede při vstupu do viewportu
 * @param once - Pokud je true, sledování po prvním zobrazení skončí (výchozí: true)
 */
export const onElementInViewport = (
    element: HTMLElement,
    callback: () => void,
    once: boolean = true
): IntersectionObserver => {
    // Konfigurace observeru
    const options: IntersectionObserverInit = {
        root: null,
        threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                callback();

                if (once) {
                    observerInstance.unobserve(element);
                }
            }
        });
    }, options);

    observer.observe(element);

    return observer;
};

/**
 * Zjistí, zda je element aktuálně viditelný ve viewportu.
 * @param element - HTML element k ověření
 * @returns boolean - true, pokud je alespoň část elementu vidět
 */
export const isElementInViewport = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();

    // Kontrola, zda jsou hranice elementu uvnitř hranic okna prohlížeče
    const isInViewport = (
        rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
        rect.bottom > 0 &&
        rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
        rect.right > 0
    );

    return isInViewport;
};

export const getInitials = (name: string): string => {
    const parts: string[] = name.trim().split(/\s+/);
    return parts
        .map((part: string) => part.charAt(0).toUpperCase())
        .join("");
};

export function parseNumber(value: string | number): number {
    if (typeof value === 'number') return value;
    return parseFloat(String(value).replace(',', '.'));
}

export function computeNutrients(
    nutrients: Partial<Record<keyof Nutrients, number | string>>,
    value: number,
): Partial<Nutrients> {
    const factor = value / 100;
    nutrients = {
        energy: 0,
        protein: 0,
        carbohydrate: 0,
        fatSat: 0,
        fatUnsat: 0,
        ...(nutrients ?? {})
    };

    const scale = (v: number | string | undefined): number | undefined => {
        if (v === undefined || v === null) return undefined;
        const n = parseNumber(v);
        return isNaN(n) ? undefined : n * factor;
    };

    const result: Partial<Nutrients> = {};

    const e = scale(nutrients.energy);
    const p = scale(nutrients.protein);
    const c = scale(nutrients.carbohydrate);
    if (e !== undefined) result.energy = e;
    if (p !== undefined) result.protein = p;
    if (c !== undefined) result.carbohydrate = c;

    const fs = scale(nutrients.fatSat);
    const fu = scale(nutrients.fatUnsat);
    if (fs !== undefined) result.fatSat = fs;
    if (fu !== undefined) result.fatUnsat = fu;

    return result;
}

export const isToday = (date: Date | string): boolean => {
    const d = typeof date === 'string' ? new Date(date) : date;

    // Kontrola validity (pokud string není validní datum, getTime vrátí NaN)
    if (isNaN(d.getTime())) {
        return false;
    }

    const today = new Date();

    return (
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear()
    );
};